import { FileExclamationOutlined } from "@ant-design/icons"
import { Button, Modal, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { PROJECT_STATUSES } from "../constants/project-statuses.ts"

const { Text, Title } = Typography

export type ClosingStatus =
  | typeof PROJECT_STATUSES.COMPLETED
  | typeof PROJECT_STATUSES.CANCELLED

type UnpaidInvoicesDialogProps = {
  open: boolean
  count: number
  busy?: boolean
  onClose: () => void
} & (
  | {
      // Asked before a close files the project in the Archive with money owed.
      mode: "close"
      status: ClosingStatus
      onConfirm: () => void
    }
  | {
      // Said when Delete is pressed on an archived project still owed money.
      mode: "delete"
    }
  | {
      // Asked once before a bulk close that would file some of the selected
      // projects in the Archive with money owed; `count` is how many of them.
      mode: "bulk-close"
      status: ClosingStatus
      selectedCount: number
      projects: UnpaidProjectLabel[]
      onConfirm: () => void
      onSkip: () => void
    }
)

export interface UnpaidProjectLabel {
  id: string
  identifier?: string | null
  name: string
}

// Enough to recognise the projects without the dialog outgrowing the screen.
const LISTED_PROJECTS_CAP = 5

/**
 * One calm, centred dialog for both moments unpaid invoices matter to a
 * project leaving the active list: the close asks (once for a whole bulk
 * selection), the delete explains.
 */
export function UnpaidInvoicesDialog(props: UnpaidInvoicesDialogProps) {
  const { t } = useTranslation()
  const { open, count, busy = false, onClose } = props

  const statusLabel =
    props.mode !== "delete"
      ? props.status === PROJECT_STATUSES.COMPLETED
        ? t("projectUnpaid.statusCompleted")
        : t("projectUnpaid.statusCancelled")
      : ""

  return (
    <Modal
      open={open}
      centered
      width={props.mode === "bulk-close" ? 520 : 440}
      footer={null}
      closable={!busy}
      keyboard={!busy}
      maskClosable={!busy}
      onCancel={onClose}
      destroyOnHidden
    >
      <div className="flex flex-col items-center gap-3 px-2 pt-2 text-center">
        <span
          className="flex size-14 items-center justify-center rounded-full bg-amber-50 text-2xl text-amber-600"
          aria-hidden
        >
          <FileExclamationOutlined />
        </span>
        <Title level={4} className="!m-0">
          {props.mode === "delete"
            ? t("projectUnpaid.deleteTitle")
            : t("projectUnpaid.closeTitle")}
        </Title>
        <Text type="secondary" className="text-[15px]">
          {props.mode === "close"
            ? t("projectUnpaid.closeBody", { count, status: statusLabel })
            : props.mode === "bulk-close"
              ? count < props.selectedCount
                ? t("projectUnpaid.bulkCloseBody", {
                    count,
                    selected: props.selectedCount,
                    status: statusLabel
                  })
                : t("projectUnpaid.bulkCloseBodyAll", {
                    count,
                    status: statusLabel
                  })
              : t("projectUnpaid.deleteBody", { count })}
        </Text>
        {props.mode === "bulk-close" ? (
          <UnpaidProjectList projects={props.projects} />
        ) : null}
      </div>
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
        {props.mode === "bulk-close" ? (
          <>
            <Button size="large" onClick={onClose} disabled={busy}>
              {t("projectUnpaid.ignore")}
            </Button>
            {/* Skipping every selected project would send nothing at all. */}
            {count < props.selectedCount ? (
              <Button size="large" onClick={props.onSkip} disabled={busy}>
                {t("projectUnpaid.bulkSkip", { count })}
              </Button>
            ) : null}
            <Button
              size="large"
              type="primary"
              loading={busy}
              onClick={props.onConfirm}
            >
              {t("projectUnpaid.bulkMoveAll", { status: statusLabel })}
            </Button>
          </>
        ) : props.mode === "close" ? (
          <>
            <Button size="large" onClick={onClose} disabled={busy}>
              {t("projectUnpaid.ignore")}
            </Button>
            <Button
              size="large"
              type="primary"
              loading={busy}
              onClick={props.onConfirm}
            >
              {t("projectUnpaid.moveTo", { status: statusLabel })}
            </Button>
          </>
        ) : (
          <Button size="large" type="primary" onClick={onClose}>
            {t("projectUnpaid.ok")}
          </Button>
        )}
      </div>
    </Modal>
  )
}

function UnpaidProjectList({ projects }: { projects: UnpaidProjectLabel[] }) {
  const { t } = useTranslation()
  const listed = projects.slice(0, LISTED_PROJECTS_CAP)
  const hidden = projects.length - listed.length

  return (
    <ul
      aria-label={t("projectUnpaid.bulkListLabel")}
      className="m-0 w-full list-none rounded-lg border border-solid border-slate-200 bg-slate-50 px-4 py-2 text-left text-sm"
    >
      {listed.map((project) => (
        <li key={project.id} className="truncate py-0.5">
          {project.identifier ? (
            <Text type="secondary" className="mr-2 tabular-nums">
              {project.identifier}
            </Text>
          ) : null}
          <Text>{project.name}</Text>
        </li>
      ))}
      {hidden > 0 ? (
        <li className="py-0.5">
          <Text type="secondary">
            {t("projectUnpaid.bulkMore", { count: hidden })}
          </Text>
        </li>
      ) : null}
    </ul>
  )
}
